import { useRef, useState, type ChangeEvent } from 'react';
import { useAppData } from '../app/AppProvider';
import { Icon } from '../components/Icon';
import { TopBar } from '../components/TopBar';
import { METRO_STATIONS } from '../data/metro';
import { BackupValidationError, parseBackup, serializeBackup } from '../domain/backup';

type Message = { kind: 'success' | 'error'; text: string } | null;

export function SettingsPage() {
  const { data, replaceAll } = useAppData();
  const inputRef = useRef<HTMLInputElement>(null);
  const [message, setMessage] = useState<Message>(null);
  const [importing, setImporting] = useState(false);

  const exportData = async () => {
    setMessage(null);
    try {
      const createdAt = new Date().toISOString();
      const json = serializeBackup(data, createdAt);
      const fileName = `mesta-backup-${createdAt.slice(0, 10)}.json`;
      const file = new File([json], fileName, { type: 'application/json' });

      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: 'Резервная копия приложения «Места»',
          });
          setMessage({ kind: 'success', text: 'Резервная копия передана в системное меню.' });
          return;
        } catch (cause) {
          if (cause instanceof DOMException && cause.name === 'AbortError') return;
          console.warn('Системное меню «Поделиться» недоступно, используется скачивание.', cause);
        }
      }

      downloadFile(file, fileName);
      setMessage({ kind: 'success', text: 'Резервная копия сохранена в JSON-файл.' });
    } catch (cause) {
      console.error(cause);
      setMessage({ kind: 'error', text: 'Не удалось создать резервную копию.' });
    }
  };

  const importData = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setMessage(null);
    setImporting(true);
    try {
      if (file.size > 10 * 1024 * 1024) throw new Error('Файл больше 10 МБ. Выберите резервную копию приложения «Места».');
      const backup = parseBackup(await file.text(), METRO_STATIONS);
      const confirmed = window.confirm(
        `Заменить текущие данные резервной копией?\n\nВ копии: ${backup.data.places.length} мест и ${backup.data.favoriteStationIds.length} избранных станций. Текущие данные будут полностью заменены.`,
      );
      if (!confirmed) return;

      await replaceAll(backup.data);
      setMessage({ kind: 'success', text: `Импорт завершён. Восстановлено мест: ${backup.data.places.length}.` });
    } catch (cause) {
      console.error(cause);
      const detail = cause instanceof BackupValidationError
        ? cause.errors.slice(0, 3).join(' ')
        : cause instanceof Error ? cause.message : '';
      setMessage({ kind: 'error', text: `Не удалось импортировать резервную копию.${detail ? ` ${detail}` : ''}` });
    } finally {
      setImporting(false);
    }
  };

  return (
    <main className="page">
      <TopBar title="Настройки" />
      <div className="settings-stack">
        <div className="notice">
          Данные хранятся только на этом устройстве. Периодически экспортируйте резервную копию, особенно перед очисткой Safari или сменой iPhone.
        </div>

        {message ? <div className={`notice ${message.kind === 'success' ? 'notice--success' : 'notice--danger'}`} role="status">{message.text}</div> : null}

        <section className="settings-card">
          <h2>Резервная копия</h2>
          <p>Экспорт включает все места, избранные станции и созданные теги. Сейчас сохранено мест: {data.places.length}.</p>
          <div className="settings-actions">
            <button className="button" type="button" onClick={() => void exportData()}><Icon name="share" /> Экспортировать JSON</button>
            <button className="button button--secondary" type="button" disabled={importing} onClick={() => inputRef.current?.click()}><Icon name="upload" /> {importing ? 'Проверка файла…' : 'Импортировать копию'}</button>
          </div>
          <input ref={inputRef} className="file-input" type="file" accept="application/json,.json" onChange={(event) => void importData(event)} />
        </section>

        <section className="settings-card">
          <h2>О приложении</h2>
          <p>«Места» работает без регистрации и сервера. Интернет нужен только для открытия сохранённых ссылок в Яндекс Картах.</p>
        </section>
      </div>
    </main>
  );
}

function downloadFile(file: File, fileName: string): void {
  const url = URL.createObjectURL(file);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
}
